import { Component } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs';
import { filter, mergeMap, take } from 'rxjs/operators';

import { BaseSearchComponent } from '../../../base/base-search.component';
import { HistoryComponent } from '../../../shared/components/history/history.component';
import { ApproveStatusEnum } from '../../../shared/enum/approve-status.enum';
import { ModalButtonResponseEnum } from '../../../shared/enum/modal-button-response.enum';
import { NewRequestStatusEnum } from '../../../shared/enum/request-status.enum';
import { RequestPageModesEnum, RequestTypeEnum } from '../../../shared/enum/request-step.enum';
import { AlertModalComponent } from '../../../shared/layouts';
import { ConfirmModalComponent } from '../../../shared/layouts/modals/confirm-modal/confirm-modal.component';
import { ConfirmWithMessageModalComponent } from '../../../shared/layouts/modals/confirm-with-message-modal/confirm-with-message-modal.component';
import { ChildItem } from '../../../shared/layouts/modals/full-modal/child-item';
import { FullModalComponent } from '../../../shared/layouts/modals/full-modal/full-modal.component';
import { ErrorResponse, PortalModule, RouteLinkTab, ShelfItems, TaskModuleUrl } from '../../../shared/models';
import { AuditLog, HistoryType } from '../../../shared/models/audit-log.model';
import * as filterDropdown from '../../../shared/models/list-value/list-key-value.model';
import { ShelfRequestList, ShelfRequestListSearchCriteria } from '../../../shared/models/shelf-request.model';
import { AuthGuardService } from '../../../shared/services';
import { ShelfRequestService } from '../../../shared/services/shelf-request.service';
import { ShelfItemsRequestAction } from '../../../shared/store/actions/first-lot-order.action';
import {
  ShelfDeleteRequestAction,
  ShelfDeleteResetAction,
  ShelfRequestListHistoryRequestAction,
  ShelfRequestListHistoryResponseAction,
  ShelfRequestListRequestAction
} from '../../../shared/store/actions/shelf-request.actions';
import { ShelfRequestState } from '../../../shared/store/reducers/shelf-request.reducers';
import { selectShelfItems } from '../../../shared/store/selectors/first-lot-order.selector';
import {
  selectAllShelfRequestList,
  selectShelfDeleteStatus,
  selectShelfRequestHistory,
  selectShelfRequestList,
  selectShelfRequestListCriteria
} from '../../../shared/store/selectors/shelf-request.selectors';
import { AppStates } from '../../../shared/store/state/app.states';
import {
  dateStringToTagCriteria,
  dateToStringCriteria,
  generateDateStringTag
} from '../../../shared/utils/date-util';
import { ModuleUtil } from '../../../shared/utils/module-util';
import { ShelfRequestComponent } from '../shelf-request/shelf-request.component';

@Component({
  selector: 'app-shelf-request-list',
  templateUrl: './shelf-request-list.component.html',
  styleUrls: ['./shelf-request-list.component.scss']
})
export class ShelfRequestListComponent extends BaseSearchComponent<
  ShelfRequestListSearchCriteria,
  ShelfRequestList,
  ShelfRequestState
> {
  private localStore: Observable<any>;

  public listRoute: Array<RouteLinkTab>;

  public minDate: Date;
  public maxDate: Date;
  public dateTag: string;
  public dateStringTag: string;
  public requestTypeTag: string;
  public requestTypeStringTag: string;
  public shelfTypeTag: string;
  public shelfTypeStringTag: string;

  public type: RequestTypeEnum;

  public shelfStatusList = filterDropdown.shelfRequestStatusFilter;
  public requestTypeList = filterDropdown.newRequestTypeFilter;
  public shelfTypeList: ShelfItems[];

  public auditLogs$: Observable<AuditLog[]>;

  constructor(
    protected readonly store: Store<AppStates>,
    protected readonly modalService: BsModalService,
    protected fb: UntypedFormBuilder,
    protected authGuardService: AuthGuardService,
    protected readonly translate: TranslateService,
    protected readonly shelfRequestService: ShelfRequestService
  ) {
    super(store, modalService, selectAllShelfRequestList, selectShelfRequestList);
  }

  doInit() {
    this.localStore = this.store.pipe(untilComponentDestroyed(this));
    this.localStore
      .pipe(select(selectShelfRequestListCriteria))
      .subscribe(criteriaObject => (this.currentPage = criteriaObject.page + 1));
    this.localStore.pipe(select(selectShelfDeleteStatus)).subscribe(isDeleteSuccess => {
      if (isDeleteSuccess) {
        this.alertSuccessModal('The request has been deleted.');
        this.doSearch(this.criteriaObject);
        this.store.dispatch(new ShelfDeleteResetAction());
      }
    });

    this.store.dispatch(new ShelfItemsRequestAction());
    this.localStore
      .pipe(
        select(selectShelfItems),
        filter(res => Boolean(res))
      )
      .subscribe(res => {
        if (res.response) {
          this.shelfTypeList = [...res.response];
        }
      });

    this.auditLogs$ = this.localStore.pipe(select(selectShelfRequestHistory));
  }

  doDestroy() {
    // intentionally empty
  }

  createForm() {
    this.searchForm = this.fb.group({
      searchCriteria: [null],
      status: [this.shelfStatusList[0].value],
      requestedDateFrom: [null],
      requestedDateTo: [null],
      requestType: [null],
      shelfType: [null]
    });
  }

  onchangeStatus(event: any) {
    this.currentPage = 1;
    this.criteriaObject = {
      ...this.criteriaObject,
      status: event.value,
      page: 0
    };
    this.doSearch(this.criteriaObject);
  }

  setInitialCriteriaObject() {
    this.criteriaObject = {
      searchCriteria: null,
      page: 0,
      size: 20
    };
  }

  clearFilterDate() {
    this.setFirstPage();
    this.searchForm.controls['requestedDateFrom'].reset();
    this.searchForm.controls['requestedDateTo'].reset();
    this.criteriaObject = {
      ...this.criteriaObject,
      page: 0,
      requestedDateFrom: null,
      requestedDateTo: null
    };
    this.search(this.criteriaObject);
  }

  clearFilterRequestType() {
    this.setFirstPage();
    this.searchForm.controls['requestType'].reset();
    this.criteriaObject = {
      ...this.criteriaObject,
      page: 0,
      requestType: null
    };
    this.search(this.criteriaObject);
  }

  clearFilterShelfType() {
    this.setFirstPage();
    this.searchForm.controls['shelfType'].reset();
    this.criteriaObject = {
      ...this.criteriaObject,
      page: 0,
      shelfType: null
    };
    this.search(this.criteriaObject);
  }

  clearAdvanceFilter() {
    this.searchForm.controls['requestedDateFrom'].reset();
    this.searchForm.controls['requestedDateTo'].reset();
    this.searchForm.controls['requestType'].reset();
    this.searchForm.controls['shelfType'].reset();

    this.setFirstPage();
    this.criteriaObject = {
      ...this.criteriaObject,
      requestedDateFrom: null,
      requestedDateTo: null,
      requestType: null,
      shelfType: null,
      page: 0
    };
    this.search(this.criteriaObject);
  }

  onSubmit() {
    this.setFirstPage();
    const formValue = this.searchForm.value;
    this.criteriaObject = {
      ...this.criteriaObject,
      searchCriteria: formValue.searchCriteria,
      page: 0
    };
    this.doSearch(this.criteriaObject);
  }

  onChangeDateFrom(value: Date): void {
    if (value && !isNaN(value.getTime())) {
      this.minDate = value;
    } else {
      this.minDate = new Date(2019, 0, 1);
    }
  }

  onChangeDateTo(value: Date): void {
    if (value && !isNaN(value.getTime())) {
      this.maxDate = value;
    } else {
      this.maxDate = new Date();
      this.maxDate.setDate(this.maxDate.getDate() + 365);
    }
  }

  onAdvanceSubmit() {
    const formValue = this.searchForm.value;
    if (
      !formValue.requestedDateFrom &&
      !formValue.requestedDateTo &&
      !formValue.requestType &&
      !formValue.shelfType
    ) {
      return;
    }

    let dateFrom = this.searchForm.value.requestedDateFrom;
    let dateTo = this.searchForm.value.requestedDateTo;

    if (dateFrom && !isNaN(dateFrom.getTime())) {
      dateFrom = dateToStringCriteria(dateFrom);
    } else {
      dateFrom = null;
    }

    if (dateTo && !isNaN(dateTo.getTime())) {
      dateTo = dateToStringCriteria(dateTo, false);
    } else {
      dateTo = null;
    }

    this.isShowAdvanceSearch = false;
    this.setFirstPage();

    this.criteriaObject = {
      ...this.criteriaObject,
      requestedDateFrom: dateFrom,
      requestedDateTo: dateTo,
      requestType:
        formValue.requestType && formValue.requestType.length > 0 ? formValue.requestType.toString() : null,
      shelfType: formValue.shelfType && formValue.shelfType.length > 0 ? formValue.shelfType.toString() : null,
      page: 0
    };
    this.search(this.criteriaObject);
  }

  prepareSearchCriteriaTags() {
    this.dateTag = null;
    this.dateStringTag = null;
    this.requestTypeTag = null;
    this.requestTypeStringTag = null;
    this.shelfTypeTag = null;
    this.shelfTypeStringTag = null;

    const requestedDateFrom = dateStringToTagCriteria(this.criteriaObject.requestedDateFrom);
    const requestedDateTo = dateStringToTagCriteria(this.criteriaObject.requestedDateTo);
    const requestedDate = generateDateStringTag({
      dateName: 'Requested Date',
      dateFrom: requestedDateFrom,
      dateTo: requestedDateTo
    });

    this.dateStringTag = requestedDate.dateStringTag;
    this.dateTag = requestedDate.dateTag;

    if (this.criteriaObject.requestType && this.criteriaObject.requestType.length > 0) {
      this.requestTypeStringTag = 'Request Type';

      const types = this.requestTypeList
        .filter(data => {
          return this.criteriaObject.requestType.indexOf(data.value) > -1;
        })
        .map(status => status.label)
        .join(', ');

      this.requestTypeTag = `"${types}"`;
    }

    if (this.criteriaObject.shelfType && this.criteriaObject.shelfType.length > 0) {
      this.shelfTypeStringTag = 'Shelf Type';

      const types = this.shelfTypeList
        .filter(data => {
          return this.criteriaObject.shelfType.indexOf(data.code) > -1;
        })
        .map(data => data.name)
        .join(', ');

      this.shelfTypeTag = `"${types}"`;
    }
  }

  search(criteriaObj) {
    this.prepareSearchCriteriaTags();
    this.store.dispatch(new ShelfRequestListRequestAction(criteriaObj));
  }

  goToCreate() {
    const mode = RequestPageModesEnum.REQUEST_CREATE;
    const initialState = {
      title: null,
      childItem: new ChildItem(
        ShelfRequestComponent,
        {
          title: ShelfRequestListComponent.getShelfRequestTitle(mode),
          mode,
          requestId: null
        },
        false
      )
    };

    this.modalService.show(FullModalComponent, {
      animated: false,
      backdrop: false,
      initialState
    });
  }

  goToView(item) {
    const mode = RequestPageModesEnum.REQUEST_VIEW;
    const initialState = ModuleUtil.InitialState(
      item,
      ShelfRequestComponent,
      mode,
      ShelfRequestListComponent.getShelfRequestTitle(mode),
      TaskModuleUrl.SHELF_REQUEST,
      PortalModule.SHELF_REQUEST
    );

    this.modalService.show(FullModalComponent, {
      animated: false,
      backdrop: false,
      initialState
    });
  }

  goToEdit(item) {
    const mode = RequestPageModesEnum.REQUEST_EDIT;
    const initialState = {
      title: null,
      childItem: new ChildItem(
        ShelfRequestComponent,
        {
          title: ShelfRequestListComponent.getShelfRequestTitle(mode),
          mode,
          requestId: item.id
        },
        false
      )
    };

    this.modalService.show(FullModalComponent, {
      animated: false,
      backdrop: false,
      initialState
    });
  }

  setRouteTab() {
    const hasListPagePermission = this.authGuardService.checkPermission(['shelf_v', 'shelf_m', 'shelf_firstlot_m']);
    const hasRequestPagePermission = this.authGuardService.checkPermission([
      'shelf_m',
      'shelf_app',
      'shelf_firstlot_m'
    ]);

    this.listRoute = [];

    if (hasListPagePermission) {
      this.listRoute.push({ tabName: 'Shelf List', url: '/products/shelf-list' });
    }

    if (hasRequestPagePermission) {
      this.listRoute.push({ tabName: 'Shelf Request', url: '/products/shelf-request-list' });
    }
  }

  doAfterVersionAlertModal() {
    this.doSearch(this.criteriaObject);
  }

  cancelShelfRequest(requestNo: string) {
    const confirmModalRef = this.modalService.show(ConfirmWithMessageModalComponent, {
      initialState: {
        title: 'Confirm',
        message: `Are you sure you want to cancel request number <strong>&quot;${requestNo}&quot;</strong>?`,
        label: 'Reason',
        okText: 'Yes, cancel',
        cancelText: 'Cancel',
        isRequiredConfirmMessage: true
      }
    });

    const modal = confirmModalRef.content.action
      .pipe(
        untilComponentDestroyed(this),
        filter(result => result === ModalButtonResponseEnum.OK),
        mergeMap(() => {
          return this.shelfRequestService
            .approveRequest({
              requestNo,
              status: ApproveStatusEnum.CANCELLED,
              comment: confirmModalRef.content.confirmMessage
            })
            .pipe(untilComponentDestroyed(this));
        })
      )
      .subscribe({
        next: () => {
          this.alertSuccessModal('The request has been cancelled.');
          this.doSearch(this.criteriaObject);
        },
        error: error => {
          this.alertErrorModal(error.error);
        },
        complete: () => {
          if (modal) {
            modal.unsubscribe();
          }
        }
      });
  }

  alertErrorModal(errorResponse: ErrorResponse) {
    const initialState = {
      title: 'Failed',
      message: this.translate.instant(errorResponse.translateKey, { context: errorResponse.message })
    };

    this.modalService.show(AlertModalComponent, {
      initialState
    });
  }

  deleteShelfRequest(id) {
    const confirmModalRef = this.modalService.show(ConfirmModalComponent, {
      initialState: {
        title: 'Confirm',
        okText: 'Yes, delete',
        cancelText: 'Cancel',
        message: 'Are you sure you want to delete this request?'
      }
    });

    const modal = confirmModalRef.content.action
      .pipe(
        untilComponentDestroyed(this),
        filter(result => result === ModalButtonResponseEnum.OK)
      )
      .subscribe({
        next: () => {
          this.store.dispatch(new ShelfDeleteRequestAction({ requestId: id }));
        },
        error: () => {
          if (modal) {
            modal.unsubscribe();
          }
        }
      });
  }

  alertSuccessModal(message: string) {
    const initialState = {
      title: 'Success',
      message
    };

    const alertModal = this.modalService.show(AlertModalComponent, {
      initialState
    });

    const modal = alertModal.content.action.pipe(untilComponentDestroyed(this)).subscribe(() => {
      this.store.dispatch(new ShelfRequestListRequestAction(this.criteriaObject));
      if (modal) {
        modal.unsubscribe();
      }
    });
  }

  showHistory(requestId: string, requestNo: string) {
    this.store.dispatch(new ShelfRequestListHistoryRequestAction({ requestId }));

    const initialState = {
      title: 'History',
      historyHeader: `Request No.: ${requestNo}`,
      action: HistoryType.REQUEST,
      historyType: HistoryType.SHELF_REQUEST,
      auditLogs$: this.auditLogs$
    };
    this.modalService.show(HistoryComponent, {
      initialState
    });

    this.modalService.onHide.pipe(take(1)).subscribe(() => {
      this.store.dispatch(new ShelfRequestListHistoryResponseAction({ auditLogs: null }));
    });
  }

  getColorStatus(status: string): string {
    return status && status.toLocaleLowerCase();
  }

  hasEditPermission(status: NewRequestStatusEnum): boolean {
    return (
      ([NewRequestStatusEnum.DRAFT, NewRequestStatusEnum.AWAITING_APPROVAL].includes(status) &&
        this.authGuardService.checkPermission(['shelf_m'])) ||
      (status === NewRequestStatusEnum.AWAITING_FIRST_LOT &&
        this.authGuardService.checkPermission(['shelf_firstlot_m']))
    );
  }

  hasDeletePermission(): boolean {
    return this.authGuardService.checkPermission(['shelf_m']);
  }

  hasCancelPermission(): boolean {
    return this.authGuardService.checkPermission(['shelf_m']);
  }

  hasViewHistoryPermission(): boolean {
    return true;
  }

  static getShelfRequestTitle(mode: RequestPageModesEnum) {
    let title: string;
    switch (mode) {
      case RequestPageModesEnum.REQUEST_CREATE:
        title = 'Create Shelf Request';
        break;
      case RequestPageModesEnum.REQUEST_VIEW:
        title = 'View Shelf Request';
        break;
      case RequestPageModesEnum.REQUEST_EDIT:
        title = 'Edit Shelf Request';
        break;
      default:
    }
    return title;
  }
}
